import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Calendar as CalIcon, Clock, MapPin, Users, Sparkles, ArrowRight, ChevronLeft, ChevronRight, Ship, Ghost, Plane, Wind, Waves, Landmark, Target, Sun } from "lucide-react";
import Reveal from "../components/Reveal";
import GoldDivider from "../components/GoldDivider";
import { events, typeLabels, formatEventDate } from "../data/events";

const HERO =
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1800&q=80";

const parseDate = (iso) => new Date(iso + "T00:00:00");
const toLocalISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const _expIcons = {
    "deep-sea": Ship,
    haunted: Ghost,
    heli: Plane,
    balloon: Wind,
    kayak: Waves,
    museum: Landmark,
    paintball: Target,
    parasail: Sun,
};

// Custom modifiers to paint dates that have events
const Events = () => {
    const [selected, setSelected] = useState(parseDate(events[0].date));
    const [month, setMonth] = useState(parseDate(events[0].date));

    const eventsByDay = useMemo(() => {
        const m = {};
        events.forEach((e) => {
            m[e.date] = m[e.date] || [];
            m[e.date].push(e);
        });
        return m;
    }, []);

    const selectedKey = selected ? toLocalISO(selected) : null;
    const selectedEvents = selectedKey ? eventsByDay[selectedKey] || [] : [];

    const allDatesWithEvents = Object.keys(eventsByDay).map(parseDate);

    const upcoming = [...events]
        .filter((e) => parseDate(e.date) >= new Date(new Date().toDateString()))
        .sort((a, b) => a.date.localeCompare(b.date));

    return (
        <div data-testid="page-events">
            {/* ---------- HERO ---------- */}
            <section className="relative pt-44 pb-24 overflow-hidden">
                <div
                    className="absolute inset-0 opacity-40"
                    style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-brand-jet" />
                <div className="relative max-w-5xl mx-auto px-6 text-center">
                    <Reveal>
                        <p className="text-[0.7rem] tracking-[0.55em] uppercase text-brand-gold mb-6">
                            The House Calendar
                        </p>
                        <h1 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl text-brand-silver leading-[1.05]">
                            Private events, <span className="italic text-brand-champagne">curated for the few.</span>
                        </h1>
                        <p className="mt-10 text-brand-silver/75 text-lg max-w-2xl mx-auto leading-relaxed">
                            Estate showings, principal roundtables, galas, and symposia. Most attended
                            by invitation; a few open to our client network. Three convened engagements per week.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ---------- CALENDAR + SELECTED DAY ---------- */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
                    {/* Calendar */}
                    <Reveal className="lg:col-span-5">
                        <div className="bg-brand-onyx border border-brand-gold/20 p-8" data-testid="events-calendar">
                            <div className="flex items-center gap-3 mb-6">
                                <CalIcon size={18} className="text-brand-gold" />
                                <p className="text-[0.65rem] tracking-[0.45em] uppercase text-brand-silver/70">
                                    Calendar · {month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                                </p>
                            </div>
                            <DayPicker
                                mode="single"
                                selected={selected}
                                onSelect={(d) => d && setSelected(d)}
                                month={month}
                                onMonthChange={setMonth}
                                showOutsideDays={true}
                                modifiers={{
                                    hasEvent: allDatesWithEvents,
                                    noEvent: (date) => !allDatesWithEvents.some(
                                        (d) => d.toDateString() === date.toDateString()
                                    ),
                                }}
                                disabled={(date) =>
                                    !allDatesWithEvents.some(
                                        (d) => d.toDateString() === date.toDateString()
                                    )
                                }
                                modifiersClassNames={{
                                    hasEvent: "reahq-has-event",
                                    noEvent: "reahq-no-event",
                                    selected: "reahq-selected",
                                    today: "reahq-today",
                                }}
                                components={{
                                    IconLeft: () => <ChevronLeft size={16} />,
                                    IconRight: () => <ChevronRight size={16} />,
                                }}
                                className="reahq-daypicker"
                            />
                            <GoldDivider className="mt-8 mb-6" />
                            <div className="flex items-center justify-between gap-4 text-[0.65rem] tracking-[0.3em] uppercase">
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-brand-gold" />
                                    <span className="text-brand-silver/70">Event Scheduled</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="w-2 h-2 bg-brand-champagne" />
                                    <span className="text-brand-silver/70">Selected</span>
                                </div>
                            </div>
                        </div>
                    </Reveal>

                    {/* Selected day content */}
                    <Reveal delay={150} className="lg:col-span-7">
                        <p className="text-[0.65rem] tracking-[0.45em] uppercase text-brand-gold mb-3">
                            {selectedEvents.length
                                ? `${selectedEvents.length} Event${selectedEvents.length > 1 ? "s" : ""} Scheduled`
                                : "No Events on This Date"}
                        </p>
                        <h2 className="font-serif-editorial text-3xl md:text-4xl text-brand-silver mb-8">
                            {selected ? formatEventDate(selectedKey) : ""}
                        </h2>

                        {selectedEvents.length === 0 && (
                            <div
                                className="border border-white/10 p-12 text-center text-brand-silver/60 text-sm"
                                data-testid="no-events-msg"
                            >
                                The house is quiet. Select a highlighted date to view scheduled engagements.
                            </div>
                        )}

                        <div className="space-y-8">
                            {selectedEvents.map((e) => (
                                <EventDetail key={e.id} event={e} />
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <GoldDivider />

            {/* ---------- UPCOMING LIST ---------- */}
            <section className="py-24 md:py-32">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
                        <div>
                            <p className="text-[0.7rem] tracking-[0.45em] uppercase text-brand-gold mb-4">
                                The Full Season
                            </p>
                            <h2 className="font-serif-editorial text-4xl md:text-5xl text-brand-silver">
                                All Upcoming Events
                            </h2>
                        </div>
                        <Link
                            to="/contact"
                            className="text-xs tracking-[0.35em] uppercase text-brand-gold hover:text-brand-champagne transition inline-flex items-center gap-2 self-start md:self-auto"
                            data-testid="events-request-invitation"
                        >
                            Request Invitation <ArrowRight size={14} />
                        </Link>
                    </div>

                    <div className="space-y-4">
                        {upcoming.map((e, i) => (
                            <Reveal key={e.id} delay={i * 60}>
                                <button
                                    onClick={() => setSelected(parseDate(e.date))}
                                    className="w-full text-left group grid grid-cols-1 md:grid-cols-12 gap-6 border border-white/10 hover:border-brand-gold/50 bg-brand-onyx/60 hover:bg-brand-onyx transition-all duration-500 p-6 md:p-8"
                                    data-testid={`event-list-${e.id}`}
                                >
                                    <div className="md:col-span-2">
                                        <div className="font-serif-editorial text-4xl text-brand-gold leading-none">
                                            {new Date(e.date + "T00:00:00").getDate()}
                                        </div>
                                        <div className="mt-1 text-[0.65rem] tracking-[0.35em] uppercase text-brand-silver/60">
                                            {new Date(e.date + "T00:00:00").toLocaleDateString("en-US", {
                                                weekday: "short",
                                                month: "short",
                                            })}
                                        </div>
                                        <div className="mt-3 text-[0.6rem] tracking-[0.3em] uppercase text-brand-champagne">
                                            {typeLabels[e.type]}
                                        </div>
                                    </div>
                                    <div className="md:col-span-7">
                                        <h3 className="font-serif-editorial text-xl md:text-2xl text-brand-champagne group-hover:text-brand-gold transition">
                                            {e.title}
                                        </h3>
                                        <p className="mt-2 text-sm text-brand-silver/65 flex items-center gap-2 flex-wrap">
                                            <MapPin size={12} className="text-brand-gold" /> {e.location}
                                            <span className="w-1 h-1 bg-brand-gold/50 rotate-45" />
                                            <Clock size={12} className="text-brand-gold" /> {e.time}–{e.endTime}
                                        </p>
                                        <p className="mt-3 text-sm text-brand-silver/60 line-clamp-2">
                                            {e.description}
                                        </p>
                                        {e.experience && (
                                            <p className="mt-3 text-[0.62rem] tracking-[0.3em] uppercase text-brand-gold/90 flex items-center gap-2">
                                                <Sparkles size={11} /> Experience · {e.experience.name}
                                            </p>
                                        )}
                                    </div>
                                    <div className="md:col-span-3 flex md:flex-col md:items-end justify-between gap-3">
                                        <span className="text-[0.6rem] tracking-[0.3em] uppercase border border-brand-gold/50 text-brand-gold px-3 py-1.5">
                                            {e.status}
                                        </span>
                                        <div className="text-right">
                                            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-brand-silver/50">Seats Left</div>
                                            <div className="font-serif-editorial text-xl text-brand-champagne">
                                                {e.seatsRemaining}
                                                <span className="text-xs text-brand-silver/40 ml-1">/ {e.capacity}</span>
                                            </div>
                                        </div>
                                        <span className="text-[0.65rem] tracking-[0.3em] uppercase text-brand-silver/60 group-hover:text-brand-gold transition flex items-center gap-2">
                                            View <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </button>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-28 md:py-36">
                <div className="max-w-3xl mx-auto text-center px-6">
                    <Reveal>
                        <GoldDivider className="mb-10" />
                        <p className="font-cursive text-brand-gold text-5xl wordmark-glow">Request the list.</p>
                        <p className="mt-6 text-brand-silver/75 max-w-xl mx-auto">
                            Most of our engagements are private and circulated only to clients and introduced guests.
                            Request an invitation and a partner will respond personally.
                        </p>
                        <Link
                            to="/contact"
                            className="mt-10 inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase border border-brand-gold text-brand-gold px-10 py-5 hover:bg-brand-gold hover:text-brand-jet transition-all duration-500"
                            data-testid="events-cta-request"
                        >
                            Request Invitation <ArrowRight size={14} />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* local daypicker styling */}
            <style>{`
                .reahq-daypicker { --rdp-cell-size: 42px; --rdp-accent-color: #D4AF37; margin: 0; }
                .reahq-daypicker .rdp-months { justify-content: center; }
                .reahq-daypicker .rdp-caption { color: #F4E3C5; padding: 0 0 1rem 0; font-family: 'Cormorant Garamond', serif; }
                .reahq-daypicker .rdp-caption_label { font-size: 1.35rem; letter-spacing: 0.06em; text-transform: uppercase; color: #E0E0E0; font-weight: 400; }
                .reahq-daypicker .rdp-nav_button { color: #D4AF37; border: 1px solid rgba(212,175,55,0.3); border-radius: 0; width: 32px; height: 32px; background: transparent; }
                .reahq-daypicker .rdp-nav_button:hover { background: rgba(212,175,55,0.1); }
                .reahq-daypicker .rdp-head_cell { color: #8A8D91; font-size: 0.65rem; letter-spacing: 0.3em; text-transform: uppercase; font-weight: 400; padding-bottom: 0.75rem; }
                .reahq-daypicker .rdp-day { color: #C4C4C8; border-radius: 0; border: 1px solid transparent; font-family: 'Manrope', sans-serif; font-weight: 400; transition: all 0.3s ease; }
                .reahq-daypicker .rdp-day:hover:not(.rdp-day_outside):not(.rdp-day_disabled) { background: rgba(212,175,55,0.08); color: #F4E3C5; border-color: rgba(212,175,55,0.3); }
                .reahq-daypicker .rdp-day_outside { color: #3A3A3D; pointer-events: none; }
                .reahq-daypicker .reahq-no-event,
                .reahq-daypicker .rdp-day_disabled { color: #55575A !important; background: transparent !important; border-color: transparent !important; cursor: default !important; opacity: 1 !important; }
                .reahq-daypicker .reahq-no-event:hover,
                .reahq-daypicker .rdp-day_disabled:hover { background: transparent !important; border-color: transparent !important; color: #55575A !important; }
                .reahq-daypicker .reahq-has-event { color: #F4E3C5; font-weight: 500; position: relative; background: rgba(212,175,55,0.06); border: 1px solid rgba(212,175,55,0.35); }
                .reahq-daypicker .reahq-has-event:hover { background: rgba(212,175,55,0.18); border-color: rgba(212,175,55,0.7); color: #D4AF37; }
                .reahq-daypicker .reahq-has-event::after { content: ''; position: absolute; bottom: 3px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 50%; background: #D4AF37; }
                .reahq-daypicker .rdp-day_today { color: #F4E3C5; border-color: rgba(212,175,55,0.4); }
                .reahq-daypicker .reahq-selected,
                .reahq-daypicker .rdp-day_selected { background: #D4AF37 !important; color: #050505 !important; border-color: #D4AF37; }
                .reahq-daypicker .reahq-selected::after { background: #050505; }
            `}</style>
        </div>
    );
};

// ---------- Event detail card ----------
const EventDetail = ({ event }) => {
    return (
        <article
            className="border border-brand-gold/20 bg-brand-onyx overflow-hidden"
            data-testid={`event-detail-${event.id}`}
        >
            <div className="relative aspect-[16/7] overflow-hidden">
                <img src={event.hero} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                    <span className="text-[0.6rem] tracking-[0.3em] uppercase border border-brand-gold/70 bg-black/50 backdrop-blur text-brand-gold px-3 py-1.5">
                        {typeLabels[event.type]}
                    </span>
                    <span className="text-[0.6rem] tracking-[0.3em] uppercase border border-brand-silver/40 bg-black/50 backdrop-blur text-brand-silver/90 px-3 py-1.5">
                        {event.status}
                    </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                    <h3 className="font-serif-editorial text-2xl md:text-3xl text-brand-champagne leading-tight">
                        {event.title}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-brand-silver/80">
                        <span className="flex items-center gap-1.5">
                            <Clock size={12} className="text-brand-gold" /> {event.time} – {event.endTime}
                        </span>
                        <span className="flex items-center gap-1.5">
                            <MapPin size={12} className="text-brand-gold" /> {event.location}
                        </span>
                        <span className="flex items-center gap-1.5">
                            <Users size={12} className="text-brand-gold" /> {event.capacity} seats
                        </span>
                    </div>
                </div>
            </div>

            <div className="p-7 md:p-9 space-y-8">
                {/* Seats availability bar */}
                <div data-testid={`event-seats-${event.id}`}>
                    <div className="flex items-end justify-between mb-2">
                        <p className="text-[0.62rem] tracking-[0.45em] uppercase text-brand-gold">Seats Available</p>
                        <p className="text-sm text-brand-silver/80">
                            <span className="font-serif-editorial text-2xl text-brand-champagne mr-1">{event.seatsRemaining}</span>
                            <span className="text-xs text-brand-silver/50">/ {event.capacity}</span>
                        </p>
                    </div>
                    <div className="h-[3px] bg-white/10 overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-brand-golddeep via-brand-gold to-brand-champagne transition-all duration-1000"
                            style={{ width: `${Math.max(2, (event.seatsRemaining / event.capacity) * 100)}%` }}
                        />
                    </div>
                    <p className="mt-2 text-[0.6rem] tracking-[0.3em] uppercase text-brand-silver/50">
                        {event.seatsTaken} reserved · {Math.round((event.seatsRemaining / event.capacity) * 100)}% still open
                    </p>
                </div>

                <p className="text-brand-silver/80 leading-relaxed">{event.description}</p>

                {/* Signature Experience */}
                {event.experience && (
                    <div
                        className="border border-brand-gold/25 bg-gradient-to-br from-brand-gold/5 to-transparent p-6 md:p-7"
                        data-testid={`experience-${event.experience.key}`}
                    >
                        <div className="flex items-start gap-5">
                            <div className="w-12 h-12 shrink-0 border border-brand-gold/50 flex items-center justify-center">
                                {(() => {
                                    const Icon = _expIcons[event.experience.key] || Sparkles;
                                    return <Icon size={22} strokeWidth={1.2} className="text-brand-gold" />;
                                })()}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[0.62rem] tracking-[0.45em] uppercase text-brand-gold">
                                    Signature Experience
                                </p>
                                <h4 className="mt-2 font-serif-editorial text-2xl md:text-3xl text-brand-champagne leading-tight">
                                    {event.experience.name}
                                </h4>
                                <p className="mt-1 text-sm italic text-brand-silver/70 font-serif-editorial">
                                    {event.experience.tagline}
                                </p>
                            </div>
                        </div>

                        <p className="mt-6 text-sm text-brand-silver/80 leading-relaxed">
                            {event.experience.description}
                        </p>

                        <div className="mt-5 text-[0.7rem] tracking-[0.2em] uppercase text-brand-silver/55 border-t border-white/5 pt-4">
                            <span className="text-brand-gold/80">Included · </span>
                            {event.experience.gear}
                        </div>

                        <div className="mt-6">
                            <p className="text-[0.62rem] tracking-[0.45em] uppercase text-brand-gold mb-3">
                                Professionals Onboard
                            </p>
                            <ul className="grid sm:grid-cols-2 gap-2.5">
                                {event.experience.professionals.map((p) => (
                                    <li
                                        key={p.name}
                                        className="flex items-start gap-3 text-sm"
                                        data-testid={`pro-${p.name.replace(/\s+/g, "-")}`}
                                    >
                                        <span className="mt-1.5 w-1.5 h-1.5 bg-brand-gold rotate-45 shrink-0" />
                                        <div className="min-w-0">
                                            <span className="text-[0.58rem] tracking-[0.3em] uppercase text-brand-gold/80 block">
                                                {p.role}
                                            </span>
                                            <span className="text-brand-champagne font-serif-editorial text-base block truncate">
                                                {p.name}
                                            </span>
                                            <span className="text-[0.7rem] text-brand-silver/55">{p.cred}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {/* Activities */}
                <div>
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles size={14} className="text-brand-gold" />
                        <p className="text-[0.65rem] tracking-[0.45em] uppercase text-brand-gold">The Evening</p>
                    </div>
                    <ul className="space-y-2.5">
                        {event.activities.map((a, i) => (
                            <li key={i} className="flex gap-3 text-sm text-brand-silver/75">
                                <span className="text-brand-gold mt-1.5 w-1.5 h-1.5 bg-brand-gold rotate-45 shrink-0" />
                                <span className="leading-relaxed">{a}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Speakers */}
                <div>
                    <p className="text-[0.65rem] tracking-[0.45em] uppercase text-brand-gold mb-5">Speakers & Hosts</p>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
                        {event.speakers.map((s) => (
                            <div key={s.name} className="flex items-center gap-3" data-testid={`speaker-${s.name.replace(/\s+/g, "-")}`}>
                                <div className="w-14 h-14 overflow-hidden border border-brand-gold/30 shrink-0">
                                    <img
                                        src={s.image}
                                        alt={s.name}
                                        className="w-full h-full object-cover grayscale"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <div className="font-serif-editorial text-base text-brand-champagne truncate">{s.name}</div>
                                    <div className="text-[0.62rem] tracking-[0.25em] uppercase text-brand-silver/55 leading-snug mt-0.5">
                                        {s.title}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Sponsors */}
                <div>
                    <p className="text-[0.65rem] tracking-[0.45em] uppercase text-brand-gold mb-5">Presenting Sponsors</p>
                    <div className="flex flex-wrap gap-3">
                        {event.sponsors.map((s) => (
                            <div
                                key={s.name}
                                className="border border-brand-silver/20 px-4 py-3 bg-black/40"
                                data-testid={`sponsor-${s.name.replace(/\s+/g, "-")}`}
                            >
                                <div className="text-xs text-brand-silver font-medium">{s.name}</div>
                                <div className="text-[0.58rem] tracking-[0.3em] uppercase text-brand-gold/80 mt-1">
                                    {s.tier}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="pt-4 border-t border-white/5 flex flex-col sm:flex-row gap-3">
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase border border-brand-gold bg-brand-gold text-brand-jet px-6 py-3 hover:bg-transparent hover:text-brand-gold transition-all"
                        data-testid={`event-rsvp-${event.id}`}
                    >
                        Request Invitation <ArrowRight size={12} />
                    </Link>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase border border-white/15 text-brand-silver/80 px-6 py-3 hover:border-brand-gold hover:text-brand-gold transition-all"
                        data-testid={`event-sponsor-${event.id}`}
                    >
                        Become a Sponsor
                    </Link>
                </div>
            </div>
        </article>
    );
};

export default Events;
